import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch article from CMS
    fetch(`https://api.dev.auxo.dk/public/v2/content/artikler?slug=${slug}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((res) => {
        if (res && res.items && res.items.length > 0) {
          const item = res.items[0];
          setArticle({
            titel: item.titel,
            intro: item.intro,
            broedtekst: item.broedtekst,
            billede: (item.billede && item.billede[0]) || '',
            kategori: item.kategori,
            relateret_problemomraade: item.relateret_problemomraade || 'stress',
            forfatter: item.forfatter || 'Kim Palasz',
            dato: item.dato,
            seo_title: item.seo_title,
            meta_description: item.meta_description,
          });
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (article?.seo_title) {
      document.title = `${article.seo_title} | Kim Palasz`;
    }
  }, [article]);

  if (loading) {
    return (
      <div className="py-32 text-center text-[#6c7480]">
        <i className="fa-solid fa-spinner fa-spin text-2xl mb-3 block text-[#5A232E]" />
        Indlæser artikel...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="py-32 text-center max-w-xl mx-auto px-4">
        <h1 className="font-display text-3xl text-[#5A232E] mb-4">Artiklen blev ikke fundet</h1>
        <p className="text-sm text-[#6c7480] mb-6">Det efterspurgte indlæg eksisterer ikke.</p>
        <Link to="/artikler/" className="px-6 py-3 bg-[#5A232E] text-white rounded-xl text-sm font-medium">
          Tilbage til alle artikler
        </Link>
      </div>
    );
  }

  return (
    <article className="bg-[#ECEEF1] py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <div className="mb-8">
          <Link
            to="/artikler/"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#6c7480] hover:text-[#5A232E] transition-colors"
          >
            <i className="fa-solid fa-arrow-left text-[11px]" aria-hidden="true" />
            <span>Tilbage til alle artikler</span>
          </Link>
        </div>

        {/* Header */}
        <header className="mb-10 text-left">
          <div className="inline-block bg-[#5A232E] text-white text-xs font-semibold uppercase tracking-wider px-3.5 py-1.5 rounded-full mb-4">
            {article.kategori}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight leading-[1.15] mb-4">
            {article.titel}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-xs text-[#6c7480] pt-2 border-t border-[#D9DDE2]">
            <span className="font-medium text-[#5A232E]">Af {article.forfatter}</span>
            <span>•</span>
            <span>{article.dato}</span>
            <span>•</span>
            <span>Psykoterapeut MPF</span>
          </div>
        </header>

        {/* Featured Image */}
        {article.billede && (
          <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white mb-12 bg-[#D9DDE2]">
            <img
              src={optimizeImage(article.billede, 1200)}
              alt={article.titel}
              className="w-full h-auto max-h-[480px] object-cover"
            />
          </div>
        )}

        {/* Content Body */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs">
          <div
            className="prose prose-base sm:prose-lg max-w-none text-[#333E3C] leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: article.broedtekst }}
          />

          {/* Contextual Internal Link back to related problem area */}
          {article.relateret_problemomraade && (
            <div className="mt-12 p-6 rounded-2xl bg-[#ECEEF1] border border-[#D9DDE2] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#5A232E] block mb-1">
                  Relateret tema
                </span>
                <div className="font-display text-lg text-[#5A232E]">
                  Kæmper du selv med lignende udfordringer?
                </div>
                <p className="text-xs text-[#6c7480] mt-0.5">
                  Læs mere om et målrettet forløb og hvad et forløb kan hjælpe dig med.
                </p>
              </div>
              <Link
                to={`/${article.relateret_problemomraade}/`}
                className="px-5 py-2.5 rounded-xl bg-[#5A232E] text-white text-xs font-semibold hover:bg-[#431722] transition-colors shrink-0"
              >
                Gå til tema →
              </Link>
            </div>
          )}
        </div>

        {/* CTA Bridge */}
        <div className="mt-12 text-center bg-[#5A232E] text-white p-8 sm:p-10 rounded-3xl shadow-xl">
          <h3 className="font-display text-2xl text-white mb-2">
            Har du brug for en fortrolig samtale?
          </h3>
          <p className="text-xs sm:text-sm text-white/80 max-w-lg mx-auto mb-6">
            Book en 20 minutters gratis afklaringssamtale hos Kim Palasz og få overblik over dine muligheder.
          </p>
          <a
            href={BOOKING_SCREENING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_booking_click', { location: `article_${slug}` })}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
          >
            <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
            <span>Book en gratis afklaringssamtale</span>
          </a>
        </div>
      </div>
    </article>
  );
}
